import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/** The variables a single comment-like toggle carries: which comment, and whether
 * it is currently liked BY THE VIEWER (the server's `liked_by_me`) — so the toggle
 * direction is derived from server state, never a local flag. */
export interface LikeVars {
  commentId: string;
  liked: boolean;
}

/**
 * Comment Like / unlike toggle (#1836, WRITE). Mirrors useLikePost: NON-OPTIMISTIC.
 * The displayed like_count is NEVER bumped client-side — on success it invalidates
 * the post's comment thread so the row REFETCHES and the count only moves to the
 * server's new value. A fabricated local +1 is exactly the anti-pattern this avoids
 * (no hallucinated numbers).
 *
 * Comment likes live ONLY in the thread slot (`social.postComments(postId)`) — a
 * NARROWER invalidation than useLikePost's whole-`social.all` arm, because a comment's
 * engagement never renders on a feed card. The postId is passed in so the hook can
 * target that one slot; a like fired from any comment on the page refetches the
 * thread once.
 *
 * The toggle direction comes from the server's `liked_by_me`: a not-yet-liked
 * comment calls likeComment(commentId); an already-liked one calls
 * unlikeComment(commentId).
 *
 * Re-entrancy is the button's concern: each comment row mounts its OWN useLikeComment,
 * so a like in flight disables ONLY that comment's control (per-comment busy scoping),
 * and a double-click fires the mutation once. A rejected like/unlike (403 blocked /
 * network) leaves the count unchanged and surfaces on `mutation.error` for the row to
 * show honestly via role="alert".
 */
export function useLikeComment(postId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, LikeVars>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: ({ commentId, liked }: LikeVars) =>
      liked
        ? communityApi.unlikeComment(commentId)
        : communityApi.likeComment(commentId),
    onSuccess: () => {
      // A comment's like_count / liked_by_me lives only in the post's comment
      // thread — invalidate that ONE slot so it refetches the server's truth
      // (never an optimistic local edit).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.postComments(postId),
      });
    },
  });
}
